<!-- AppConfigForm.vue -->
<template>
  <div class="config-form">
    <template v-for="field in schema.fields" :key="field.key">

      <!-- URL input -->
      <div v-if="field.type === 'url'" class="setting-group">
        <label class="setting-label">
          <i :class="[field.icon, 'setting-icon', field.iconClass]"></i>
          {{ field.label }}
        </label>
        <input
          type="url"
          class="setting-input"
          :value="modelValue[field.key]"
          :placeholder="field.placeholder"
          @input="update(field.key, $event.target.value)"
        />
        <p class="setting-hint" v-html="field.hint"></p>
      </div>

      <!-- Tags input -->
      <div v-else-if="field.type === 'tags'" class="setting-group">
        <label class="setting-label">
          <i :class="[field.icon, 'setting-icon', field.iconClass]"></i>
          {{ field.label }}
        </label>
        <p v-if="field.hint" class="setting-hint mb-2" v-html="field.hint"></p>
        <div class="tag-input-wrapper">
          <div class="tags-list">
            <span
              v-for="(tag, i) in modelValue[field.key]"
              :key="i"
              class="tag"
              :class="field.iconClass === 'text-danger' ? 'tag-block' : 'tag-allow'"
            >
              {{ tag }}
              <button class="tag-remove" @click="removeTag(field.key, i)">×</button>
            </span>
          </div>
          <div class="tag-add-row">
            <input
              type="text"
              class="setting-input"
              v-model="tagInputs[field.key]"
              :placeholder="field.placeholder"
              @keydown.enter.prevent="addTag(field.key)"
            />
            <button class="btn-add" @click="addTag(field.key)">Add</button>
          </div>
        </div>
      </div>

      <!-- Toggle -->
      <div v-else-if="field.type === 'toggle'" class="setting-group">
        <label class="setting-label">
          <i :class="[field.icon, 'setting-icon', field.iconClass]"></i>
          {{ field.label }}
        </label>
        <div class="toggle-row">
          <span class="toggle-desc">{{ field.description }}</span>
          <div
            class="toggle-switch"
            :class="{ active: modelValue[field.key] }"
            @click="update(field.key, !modelValue[field.key])"
          >
            <div class="toggle-knob"></div>
          </div>
        </div>
      </div>

      <!-- Radio -->
      <div v-else-if="field.type === 'radio'" class="setting-group">
        <label class="setting-label">
          <i :class="[field.icon, 'setting-icon', field.iconClass]"></i>
          {{ field.label }}
        </label>
        <div class="radio-group">
          <label
            v-for="opt in field.options"
            :key="opt.value"
            class="radio-option"
          >
            <input
              type="radio"
              :value="opt.value"
              :checked="modelValue[field.key] === opt.value"
              @change="update(field.key, opt.value)"
            />
            <span class="radio-label">
              <span class="radio-title">{{ opt.title }}</span>
              <span class="radio-desc">{{ opt.desc }}</span>
            </span>
          </label>
        </div>
      </div>
      <!-- Select dropdown -->
      <div v-else-if="field.type === 'select'" class="setting-group">
        <label class="setting-label">
          <i :class="[field.icon, 'setting-icon', field.iconClass]"></i>
          {{ field.label }}
        </label>
        <div class="select-options">
          <label
            v-for="opt in field.options"
            :key="opt.value"
            class="select-option"
            :class="{ active: modelValue[field.key] === opt.value }"
            @click="update(field.key, opt.value)"
          >
            <span class="select-title">{{ opt.title }}</span>
            <span class="select-desc">{{ opt.desc }}</span>
          </label>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
export default {
  name: 'AppConfigForm',
  props: {
    schema:     { type: Object, required: true },  // one entry from APP_CONFIG_SCHEMAS
    modelValue: { type: Object, required: true },  // v-model bound config object
  },
  emits: ['update:modelValue'],

  data() {
    // One temp string input per tags field
    const tagInputs = {};
    this.schema.fields
      .filter(f => f.type === 'tags')
      .forEach(f => { tagInputs[f.key] = ''; });
    return { tagInputs };
  },

  methods: {
    update(key, value) {
        this.$emit('update:modelValue', { ...this.modelValue, [key]: value });
    },

    // ── Shared smart logic for allowlist/blocklist sync ──────
    syncBlocklistStar(updatedAllowlist) {
        // Find blocklist and URLAllowlist field keys from schema
        const blockField = this.schema.fields.find(f => f.key === 'URLBlocklist');
        if (!blockField) return;

        const currentBlocklist = [...(this.modelValue['URLBlocklist'] || [])];
        const hasStar = currentBlocklist.includes('*');

        if (updatedAllowlist.length > 0 && !hasStar) {
            // Auto-add * when first allowlist item added
            currentBlocklist.unshift('*');
            this.update('URLBlocklist', currentBlocklist);
        } else if (updatedAllowlist.length === 0 && hasStar) {
            // Auto-remove * when allowlist is cleared
            const starIndex = currentBlocklist.indexOf('*');
            currentBlocklist.splice(starIndex, 1);
            this.update('URLBlocklist', currentBlocklist);
        }
    },

    syncHomepageToAllowlist(updatedAllowlist) {
        // Auto-add homepage URL to allowlist if not already present
        const homepage = this.modelValue['HomepageLocation'];
        if (homepage && updatedAllowlist.length > 0 && !updatedAllowlist.includes(homepage)) {
            return [...updatedAllowlist, homepage];
        }
        return updatedAllowlist;
    },

    addTag(key) {
        const val = this.tagInputs[key].trim();
        if (!val) return;

        let updated = [...(this.modelValue[key] || [])];
        if (!updated.includes(val)) {
            updated.push(val);
        }

        // If adding to URLAllowlist — run smart sync
        if (key === 'URLAllowlist') {
            updated = this.syncHomepageToAllowlist(updated);
            this.update(key, updated);
            this.syncBlocklistStar(updated);
        } else {
            this.update(key, updated);
        }

        this.tagInputs[key] = '';
    },

    removeTag(key, index) {
        const updated = [...(this.modelValue[key] || [])];
        updated.splice(index, 1);

        // If removing from URLAllowlist — sync * in blocklist
        if (key === 'URLAllowlist') {
            this.update(key, updated);
            this.syncBlocklistStar(updated);
        } else {
            this.update(key, updated);
        }
    },
},

    watch: {
    // Sync homepage URL changes into allowlist
    'modelValue.HomepageLocation'(newVal, oldVal) {
    const allowlist = [...(this.modelValue['URLAllowlist'] || [])];
    if (!allowlist.length) return;

    // Remove old homepage entry if it was auto-added
    const oldIndex = oldVal ? allowlist.indexOf(oldVal) : -1;
    if (oldIndex !== -1) allowlist.splice(oldIndex, 1);

    // Add new homepage if non-empty and not already present
    if (newVal && !allowlist.includes(newVal)) {
        allowlist.unshift(newVal);
    }

    this.update('URLAllowlist', allowlist);
    },
    },
};
</script>
<style scoped>
.main-container {
  max-width: 480px;
  margin: 0 auto;
  font-family: 'Segoe UI', sans-serif;
}

.back-home {
  display: block;
  width: 100%;
  margin-bottom: 15px;
  font-size: 1rem;
}

/* ── Chrome Status Bar ── */
.chrome-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: #f8f9ff;
  border-bottom: 1.5px solid #e8eaf6;
}

.status-left {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.status-label-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a2e;
}

.status-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.badge-on {
  background: #e6f4ea;
  color: #1a7a3f;
  border: 1px solid #b7eacb;
}

.badge-off {
  background: #fce8e6;
  color: #c0392b;
  border: 1px solid #f5c6cb;
}

.status-hint {
  font-size: 0.72rem;
  color: #999;
}

.status-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ── Daily Limit Button ── */
.daily-limit-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  background: #fff;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 7px 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, transform 0.15s;
  color: #1a73e8;
  text-decoration: none;
}

.daily-limit-btn:hover {
  border-color: #1a73e8;
  background: #f0f6ff;
  transform: translateY(-1px);
}

.daily-limit-btn i {
  font-size: 1.1rem;
}

.daily-limit-label {
  font-size: 0.62rem;
  font-weight: 700;
  color: #1a73e8;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* ── Disabled overlay for settings body ── */
.settings-disabled {
  opacity: 0.4;
  pointer-events: none;
  user-select: none;
  position: relative;
}

.settings-disabled::after {
  content: 'Chrome is disabled';
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: #888;
  background: rgba(255,255,255,0.5);
  border-radius: 0 0 16px 16px;
  pointer-events: all;
  opacity: 1;
}

/* ── Card ── */
.settings-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
  overflow: hidden;
  margin-bottom: 20px;
}

.settings-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
  padding: 18px 20px;
  color: #fff;
}

.chrome-icon {
  font-size: 2rem;
  line-height: 1;
}

.settings-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
}

.settings-subtitle {
  margin: 2px 0 0;
  font-size: 0.8rem;
  opacity: 0.85;
  color: #fff;
}

.settings-body {
  padding: 20px;
}

/* ── Loading ── */
.loading-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 30px 20px;
  color: #666;
  justify-content: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Setting groups ── */
.setting-group {
  margin-bottom: 22px;
  padding-bottom: 22px;
  border-bottom: 1px solid #f0f0f0;
}

.setting-group:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.setting-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.setting-icon {
  font-size: 0.9rem;
  width: 18px;
  text-align: center;
  color: #555;
}

.setting-icon.text-danger { color: #dc3545; }
.setting-icon.text-success { color: #28a745; }
.setting-icon.text-primary { color: #1a73e8; }

.setting-hint {
  font-size: 0.78rem;
  color: #888;
  margin: 4px 0 0;
}

.setting-hint code {
  background: #f0f4ff;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: monospace;
  color: #1a73e8;
}

/* ── Input ── */
.setting-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #333;
  transition: border-color 0.2s;
  box-sizing: border-box;
  outline: none;
}

.setting-input:focus {
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26,115,232,0.12);
}

/* ── Tag inputs ── */
.tag-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 30px;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 500;
}

.tag-block {
  background: #fff0f0;
  color: #c0392b;
  border: 1px solid #f5c6cb;
}

.tag-allow {
  background: #f0fff4;
  color: #1a7a3f;
  border: 1px solid #b7eacb;
}

.tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  color: inherit;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.tag-remove:hover { opacity: 1; }

.tag-add-row {
  display: flex;
  gap: 8px;
}

.tag-add-row .setting-input {
  flex: 1;
}

.btn-add {
  padding: 9px 16px;
  background: #1a73e8;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
}

.btn-add:hover { background: #1558b0; }

/* ── Radio group ── */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.radio-option:has(input:checked) {
  border-color: #1a73e8;
  background: #f0f6ff;
}

.radio-option input[type="radio"] {
  margin-top: 3px;
  accent-color: #1a73e8;
  flex-shrink: 0;
}

.radio-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.radio-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
}

.radio-desc {
  font-size: 0.75rem;
  color: #888;
}

/* ── Toggle ── */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
}

.toggle-desc {
  font-size: 0.88rem;
  color: #444;
  flex: 1;
}

.toggle-switch {
  position: relative;
  width: 46px;
  height: 26px;
  background: #ccc;
  border-radius: 13px;
  cursor: pointer;
  transition: background 0.25s;
  flex-shrink: 0;
}

.toggle-switch.active {
  background: #1a73e8;
}

.toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  transition: left 0.25s;
}

.toggle-switch.active .toggle-knob {
  left: 23px;
}

/* ── Save button ── */
.save-row {
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;
}

.btn-save {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #1a73e8, #0d47a1);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  letter-spacing: 0.02em;
}

.btn-save:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
/* ── Select options ── */
.select-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border: 1.5px solid #e8e8e8;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.select-option:hover {
  border-color: #a8c7fa;
  background: #f8fbff;
}

.select-option.active {
  border-color: #1a73e8;
  background: #f0f6ff;
}

.select-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
}

.select-option.active .select-title {
  color: #1a73e8;
}

.select-desc {
  font-size: 0.75rem;
  color: #888;
}
</style>